<template>
  <div class="field-survey-form-root">
    <div class="field-survey-form-inner">
      <header class="fsf-page-header">
        <h1 class="fsf-page-title">
          <template v-if="fieldSurveyLoading">{{ $t('FieldSurveyForm.loadingTitle') }}</template>
          <template v-else-if="fieldSurveyError">{{ $t('FieldSurveyForm.titleError') }}</template>
          <template v-else>{{ pageTitle }}</template>
        </h1>
      </header>

      <div class="fsf-main-card">
        <section class="fsf-map-panel" aria-label="field-survey-map">
          <field-survey-map
            v-if="!fieldSurveyLoading && !fieldSurveyError && fieldSurveyId"
            ref="fieldSurveyMap"
            class="fsf-map-embed"
            :survey-works="equipments"
            :service-rows="serviceAssignmentRows"
            :devices-qty-number="equipmentCapNumber"
            :disabled="surveyWorksLoading || !isSetupEditable"
            :saving="mapSaving"
            :technician-items="technicianItems"
            :technician-options-loading="technicianOptionsLoading"
            :field-survey-id="fieldSurveyId"
            @create-work="onCreateSurveyWorkFromMap"
            @add-blocked="onMapAddBlocked"
            @technician-user-created="onTechnicianUserCreated"
          />
          <div v-else class="fsf-map-placeholder" />
          <div v-if="showMapHint" class="fsf-map-hint-banner" role="status">
            <v-icon small class="fsf-map-hint-icon">mdi-information</v-icon>
            <span>{{ mapHintText }}</span>
          </div>
        </section>

        <aside class="fsf-sidebar">
          <div class="fsf-sidebar-header">
            <div
              v-if="serviceAssignmentRows.length"
              class="fsf-summary"
              aria-live="polite"
            >
              <div class="fsf-summary-row">
                <span class="fsf-summary-label">{{ $t('FieldSurveyForm.summaryEquipments') }}</span>
                <span class="fsf-summary-metric">
                  <span class="fsf-summary-value">{{ equipmentCount }}</span>
                  <a
                    v-if="showHireMoreLink"
                    class="fsf-hire-more-link"
                    :href="hireMoreWhatsappUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ $t('FieldSurveyForm.hireMore') }}
                  </a>
                </span>
              </div>
              <div
                v-for="row in serviceAssignmentRows"
                :key="row.key"
                class="fsf-summary-row"
              >
                <span class="fsf-summary-label">{{ $t('EquipmentServices.' + row.key) }}</span>
                <span class="fsf-summary-value" :class="serviceCountClass(row)">
                  {{ row.assigned }} / {{ row.contracted }}
                </span>
              </div>
            </div>
            <div v-else class="fsf-sidebar-header-top">
              <span class="fsf-sidebar-count">{{ hiredEquipmentsText }}</span>
              <a
                v-if="showHireMoreLink"
                class="fsf-hire-more-link"
                :href="hireMoreWhatsappUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ $t('FieldSurveyForm.hireMore') }}
              </a>
            </div>
          </div>

          <div v-if="showEditServiceLink" class="fsf-edit-service-wrap">
            <a
              class="fsf-edit-service-link"
              href="#"
              @click.prevent="enableAdminEdit"
            >
              {{ $t('FieldSurveyForm.editService') }}
            </a>
          </div>

          <div v-if="surveyWorksLoading" class="fsf-sidebar-loading py-6 text-center">
            <v-progress-circular indeterminate color="#0091d5" size="32" />
          </div>
          <div v-else-if="surveyWorksError" class="fsf-sidebar-error caption grey--text px-2">
            {{ $t('FieldSurveyForm.surveyWorksError') }}
          </div>
          <div v-else class="fsf-sidebar-list">
            <survey-work-card
              v-for="(work, index) in equipments"
              :key="work.id"
              :work="work"
              :service-rows="serviceAssignmentRows"
              :marker-letter="markerLetter(index)"
              :saving="savingId === work.id"
              :deleting="deletingId === work.id"
              :technician-items="technicianItems"
              :technician-options-loading="technicianOptionsLoading"
              :field-survey-id="fieldSurveyId"
              :readonly="!isSetupEditable"
              @save="onSaveWork"
              @delete="onDeleteWork"
              @technician-user-created="onTechnicianUserCreated"
            />
            <p v-if="!equipmentCount" class="fsf-empty caption grey--text px-2">
              {{ $t('FieldSurveyForm.noSurveyWorks') }}
            </p>
          </div>

          <div v-if="showConfirmButton || showFinishEditButton" class="fsf-confirm-wrap">
            <v-btn
              v-if="showConfirmButton"
              class="fsf-confirm-btn"
              block
              large
              depressed
              :disabled="operationsBusy || fieldSurveyLoading || surveyWorksLoading || fieldSurveyError || surveyWorksError"
              @click="onConfirmClick"
            >
              {{ $t('FieldSurveyForm.confirm') }}
            </v-btn>
            <v-btn
              v-else
              class="fsf-confirm-btn"
              block
              large
              depressed
              :disabled="operationsBusy"
              @click="finishAdminEdit"
            >
              {{ $t('FieldSurveyForm.finishEditService') }}
            </v-btn>
          </div>
        </aside>
      </div>
    </div>

    <v-dialog
      v-model="ponceChargeConfirmDialog"
      max-width="520"
      content-class="fsf-ponce-dialog-overlay"
      @click:outside="ponceChargeConfirmDialog = false"
    >
      <v-card class="fsf-ponce-dialog-card" flat>
        <v-btn
          icon
          class="fsf-ponce-close"
          :aria-label="$t('FieldSurveyForm.cancel')"
          @click="ponceChargeConfirmDialog = false"
        >
          <v-icon color="grey darken-2">mdi-close</v-icon>
        </v-btn>
        <v-card-text class="fsf-ponce-dialog-text pt-10 pb-2 text-center">
          {{ $t('FieldSurveyForm.ponceChargeWarningBody') }}
        </v-card-text>
        <v-card-actions class="fsf-ponce-dialog-actions px-4 pb-5 pt-2">
          <v-btn
            outlined
            depressed
            class="fsf-ponce-btn-cancel"
            @click="ponceChargeConfirmDialog = false"
          >
            {{ $t('FieldSurveyForm.cancel') }}
          </v-btn>
          <v-btn
            color="#0091d5"
            dark
            depressed
            class="fsf-ponce-btn-ok"
            @click="onConfirmPonceCharges"
          >
            {{ $t('FieldSurveyForm.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transition name="fsf-toast">
      <div
        v-if="assignmentToast.show"
        class="fsf-assignment-toast"
        role="alert"
      >
        <v-icon class="fsf-assignment-toast-icon" size="18">mdi-alert-outline</v-icon>
        <div class="fsf-assignment-toast-body">
          <p class="fsf-assignment-toast-title">{{ assignmentToast.title }}</p>
          <ul v-if="assignmentToast.issues.length" class="fsf-assignment-toast-list">
            <li v-for="(issue, index) in assignmentToast.issues" :key="index">{{ issue }}</li>
          </ul>
        </div>
        <button
          type="button"
          class="fsf-assignment-toast-close"
          :aria-label="$t('FieldSurveyForm.cancel')"
          @click="hideAssignmentToast"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>
    </transition>

    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { firestore, firestoreConstructors } from '@/plugins/firebase'
import { markFieldSurveyInProgressForSetup, FIELD_SURVEY_STATUS } from '@/lib/fieldSurveysRouting'
import {
  uploadSurveyWorkPluviometricChart,
  deleteSurveyWorkPluviometricChartAtPath
} from '@/lib/surveyWorkPluviometricChart'
import {
  parseSurveyWorkUserRefId,
  fetchUsersByIds,
  formatTechnicianDisplayName,
  loadTechnicianSelectOptions,
  mergeTechnicianOption
} from '@/lib/surveyWorkTechnicians'
import { SURVEY_WORK_STATUS, SURVEY_WORK_SHOULD_NOTIFY, isSurveyWorkNewStatus } from '@/lib/surveyWorkStatus'
import { validateLatLon } from '@/lib/helpers'
import {
  serviceQuantities,
  serviceRows,
  servicesComplete,
  validServiceSelection,
  maxEquipmentSlots,
  serviceTypeFromKey,
  serviceKeyFromServiceType,
  equipmentGroupId,
  groupSurveyWorks,
  lockedServiceKeys,
  surveyWorksRemovedByServiceSelection,
  effectiveServiceKey
} from '@/lib/surveyWorkServices'
import { surveyWorkMarkerLabel } from '@/lib/surveyWorkMarkerLabel'
import { trackEvent } from '@/lib/track-events'
import { EVENTS_TO_TRACK } from '@/constants/const'
import SurveyWorkCard from './SurveyWorkCard.vue'
import FieldSurveyMap from './FieldSurveyMap.vue'

const FIELD_SURVEYS = 'app/v1/fieldSurveys'
const SURVEY_WORKS = 'app/v1/surveyWorks'

function firestoreDateToJs (v) {
  if (!v) return null
  if (typeof v.toDate === 'function') return v.toDate()
  if (v instanceof Date) return v
  return null
}

function surveyWorkTechnicianChanged (work, { userId, ponceTechnician }) {
  if (!work) return false
  const nextPonce = !!ponceTechnician
  const prevPonce = !!work.ponceTechnician
  if (nextPonce !== prevPonce) return true
  if (nextPonce) return false
  return String(userId || '').trim() !== String(work.userId || '').trim()
}

function technicianFields (db, { userId, ponceTechnician }) {
  if (ponceTechnician) return { user: null, ponceTechnician: true }
  const uid = String(userId || '').trim()
  return {
    ponceTechnician: false,
    user: uid ? db.collection('app/v1/users').doc(uid) : null
  }
}

function chartPaths (works) {
  return [...new Set((works || []).map((work) => String(work.pluviometricChart || '').trim()).filter(Boolean))]
}

async function deleteUnusedCharts (previousPaths, nextChartPath) {
  const kept = String(nextChartPath || '').trim()
  const unused = (previousPaths || []).filter((path) => path && path !== kept)
  await Promise.all(unused.map((path) => deleteSurveyWorkPluviometricChartAtPath(path)))
}

export default {
  name: 'FieldSurveyForm',
  components: { SurveyWorkCard, FieldSurveyMap },
  data () {
    return {
      fieldSurveyLoading: true,
      fieldSurveyError: false,
      fieldSurveyCreatedAt: null,
      fieldSurveyDealId: '',
      fieldSurveyStatus: '',
      devicesQty: null,
      contractedServices: null,
      hasServiceQuantitiesField: false,
      adminEditMode: false,
      surveyWorksLoading: true,
      surveyWorksError: false,
      surveyWorks: [],
      unsubscribeSurveyWorks: null,
      mapSaving: false,
      savingId: null,
      deletingId: null,
      technicianItems: [],
      technicianOptionsLoading: false,
      ponceChargeConfirmDialog: false,
      assignmentToast: {
        show: false,
        title: '',
        issues: []
      },
      assignmentToastTimer: null,
      snackbar: {
        show: false,
        text: '',
        timeout: 4000,
        color: 'success'
      }
    }
  },
  computed: {
    operationsBusy () { return !!(this.mapSaving || this.savingId || this.deletingId) },
    equipments () { return groupSurveyWorks(this.surveyWorks) },
    equipmentCount () { return this.equipments.length },
    serviceAssignmentRows () {
      return serviceRows(this.contractedServices, this.surveyWorks)
        .filter(row => row.contracted > 0 || row.assigned > 0)
    },
    assignmentsComplete () { return servicesComplete(this.contractedServices, this.surveyWorks) },
    // Cap from service slots (sum). Overlap means fewer equipment than this max — that is valid.
    equipmentCapNumber () {
      if (this.contractedServices) return maxEquipmentSlots(this.contractedServices)
      return this.devicesQtyNumber
    },
    equipmentCountMatches () {
      const cap = this.equipmentCapNumber
      if (cap === null) return false
      if (this.contractedServices) {
        return this.equipmentCount > 0 && this.equipmentCount <= cap
      }
      return this.equipmentCount === cap
    },
    fieldSurveyId () {
      return this.$route.params.id
    },
    orgName () {
      const o = this.$store.getters.userOrganization
      return (o && o.name) ? o.name : ''
    },
    organizationId () {
      const o = this.$store.getters.userOrganization
      return (o && o.id) ? o.id : ''
    },
    pageTitle () {
      const dateStr = this.formatFieldSurveyDate(this.fieldSurveyCreatedAt)
      return this.$t('FieldSurveyForm.pageTitle', {
        org: this.orgName || '—',
        date: dateStr || '—'
      })
    },
    devicesQtyNumber () {
      const q = this.devicesQty
      if (q === null || q === undefined) return null
      const n = Number(q)
      return Number.isFinite(n) ? n : null
    },
    hiredEquipmentsText () {
      // With serviceQuantities, devicesQty is irrelevant: show loaded equipment only.
      if (this.contractedServices || this.hasServiceQuantitiesField) {
        const n = this.equipmentCount
        if (n === 1) return this.$t('FieldSurveyForm.hiredEquipmentsLoadedOne')
        return this.$t('FieldSurveyForm.hiredEquipmentsLoadedMany', { count: n })
      }
      const q = this.devicesQtyNumber
      if (q === null) {
        const n = this.equipmentCount
        if (n === 1) return this.$t('FieldSurveyForm.hiredEquipmentsUnknownOne')
        return this.$t('FieldSurveyForm.hiredEquipmentsUnknown', { count: n })
      }
      if (q === 1) {
        return this.$t('FieldSurveyForm.hiredEquipmentsOne')
      }
      return this.$t('FieldSurveyForm.hiredEquipmentsMany', { count: q })
    },
    hasRemainingServiceQuota () {
      if (!this.serviceAssignmentRows.length) return true
      return this.serviceAssignmentRows.some(row => row.assigned < row.contracted)
    },
    canAddEquipmentOnMap () {
      if (!this.isSetupEditable || this.surveyWorksLoading) return false
      if (this.contractedServices) {
        const cap = this.equipmentCapNumber
        if (cap != null && this.equipmentCount >= cap) return false
        return this.hasRemainingServiceQuota
      }
      const cap = this.equipmentCapNumber
      if (cap === null) return false
      if (this.equipmentCount >= cap) return false
      return this.hasRemainingServiceQuota
    },
    showMapHint () {
      return !this.fieldSurveyLoading && !this.fieldSurveyError && this.canAddEquipmentOnMap
    },
    mapHintText () {
      const q = this.equipmentCapNumber
      const next = Math.min((this.equipmentCount || 0) + 1, q || (this.equipmentCount + 1))
      return this.$t('FieldSurveyForm.mapHint', { equipmentNumber: next })
    },
    canConfirm () {
      if (this.fieldSurveyLoading || this.surveyWorksLoading || this.fieldSurveyError) return false
      if (this.contractedServices) return this.assignmentsComplete
      return this.equipmentCountMatches
    },
    hireMoreWhatsappUrl () {
      const raw = process.env.VUE_APP_ESTEBAN_PHONE
      if (raw === undefined || raw === null || raw === '') return ''
      const digits = String(raw).replace(/\D/g, '')
      if (!digits) return ''
      const text = encodeURIComponent(this.$t('FieldSurveyForm.hireMoreWhatsappText'))
      return `https://wa.me/${digits}?text=${text}`
    },
    showHireMoreLink () {
      return !this.isFieldSurveyCompleted && this.canConfirm && !!this.hireMoreWhatsappUrl
    },
    hasAnyPonceTechnicianSurveyWork () {
      return this.surveyWorks.some((w) => w.ponceTechnician)
    },
    isPonceAdmin () {
      if (this.$store.getters.impersonatedUserId) return true
      const user = this.$store.getters.user
      return !!(user && user.isAdmin)
    },
    isFieldSurveyCompleted () {
      return this.fieldSurveyStatus === FIELD_SURVEY_STATUS.COMPLETED
    },
    isSetupEditable () {
      return !this.isFieldSurveyCompleted || (this.isPonceAdmin && this.adminEditMode)
    },
    showEditServiceLink () {
      return this.isFieldSurveyCompleted && this.isPonceAdmin && !this.adminEditMode
    },
    showFinishEditButton () {
      return this.isFieldSurveyCompleted && this.isPonceAdmin && this.adminEditMode
    },
    showConfirmButton () {
      return !this.isFieldSurveyCompleted
    }
  },
  watch: {
    fieldSurveyId: {
      immediate: true,
      handler (id) {
        this.adminEditMode = false
        this.teardownSurveyWorksListener()
        if (id) this.bootstrap(id)
      }
    }
  },
  beforeDestroy () {
    this.clearAssignmentToastTimer()
    this.teardownSurveyWorksListener()
  },
  methods: {
    serviceCountClass (row) {
      if (row.assigned > row.contracted) return 'fsf-summary-value--error'
      if (row.assigned === row.contracted) return 'fsf-summary-value--complete'
      return 'fsf-summary-value--pending'
    },
    assignmentErrorIssues (snapshot) {
      const quantities = snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'quantities')
        ? snapshot.quantities
        : this.contractedServices
      const works = (snapshot && snapshot.works) || this.surveyWorks
      const devicesQtyNumber = snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'devicesQtyNumber')
        ? snapshot.devicesQtyNumber
        : this.devicesQtyNumber
      const surveyWorksError = snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'surveyWorksError')
        ? snapshot.surveyWorksError
        : this.surveyWorksError
      const rows = serviceRows(quantities, works).filter(row => row.contracted > 0 || row.assigned > 0)
      const groups = groupSurveyWorks(works)
      const equipmentCount = groups.length
      const cap = quantities ? maxEquipmentSlots(quantities) : devicesQtyNumber
      const equipmentOverContract = cap !== null && equipmentCount > cap
      const equipmentCountMatches = cap === null
        ? false
        : (quantities ? equipmentCount > 0 && equipmentCount <= cap : equipmentCount === cap)
      const issues = []
      rows.forEach((row) => {
        if (row.assigned === row.contracted) return
        const count = Math.abs(row.contracted - row.assigned)
        const service = this.$t('EquipmentServices.' + row.key)
        if (row.assigned < row.contracted) {
          issues.push(this.$t(
            count === 1 ? 'EquipmentServices.toastMissingOne' : 'EquipmentServices.toastMissingMany',
            { service, count }
          ))
        } else {
          issues.push(this.$t(
            count === 1 ? 'EquipmentServices.toastExtraOne' : 'EquipmentServices.toastExtraMany',
            { service, count }
          ))
        }
      })
      if (quantities && groups.some(equipment => !equipment.serviceKeys.length)) {
        issues.push(this.$t('EquipmentServices.toastNeedsService'))
      }
      if (cap === null) {
        issues.push(this.$t('EquipmentServices.toastNoEquipmentQty'))
      } else if (equipmentOverContract || (!quantities && !equipmentCountMatches)) {
        issues.push(this.$t('EquipmentServices.toastEquipment', {
          assigned: equipmentCount,
          contracted: cap
        }))
      }
      if (!issues.length && surveyWorksError) {
        issues.push(this.$t('FieldSurveyForm.surveyWorksError'))
      }
      return issues
    },
    clearAssignmentToastTimer () {
      if (this.assignmentToastTimer) {
        clearTimeout(this.assignmentToastTimer)
        this.assignmentToastTimer = null
      }
    },
    hideAssignmentToast () {
      this.clearAssignmentToastTimer()
      this.assignmentToast = { show: false, title: '', issues: [] }
    },
    showAssignmentError (snapshot) {
      this.clearAssignmentToastTimer()
      this.assignmentToast = {
        show: true,
        title: this.$t('EquipmentServices.toastTitle'),
        issues: this.assignmentErrorIssues(snapshot)
      }
      this.assignmentToastTimer = setTimeout(() => {
        this.hideAssignmentToast()
      }, 5200)
    },
    validateSelection (services, id) {
      if (validServiceSelection(services, this.contractedServices, this.surveyWorks, id)) return true
      this.showAssignmentError()
      return false
    },
    validateAssignments () {
      if (this.canConfirm && this.assignmentsComplete && !this.surveyWorksError) return true
      this.showAssignmentError()
      return false
    },
    async validateServerAssignments (db) {
      const ref = db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId)
      const [survey, works] = await Promise.all([
        ref.get({ source: 'server' }),
        db.collection(SURVEY_WORKS).where('fieldSurvey', '==', ref).get({ source: 'server' })
      ])
      if (!survey.exists) throw new Error('Field survey not found')
      const data = survey.data()
      this.devicesQty = data.devicesQty
      this.hasServiceQuantitiesField = !!(data.serviceQuantities && typeof data.serviceQuantities === 'object')
      this.contractedServices = serviceQuantities(data)
      const items = works.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      const loadedEquipments = groupSurveyWorks(items).length
      const snapshot = {
        quantities: this.contractedServices,
        works: items,
        devicesQtyNumber: this.devicesQtyNumber,
        surveyWorksError: false
      }
      if (this.contractedServices) {
        const cap = maxEquipmentSlots(this.contractedServices)
        if (loadedEquipments > cap || !servicesComplete(this.contractedServices, items)) {
          this.showAssignmentError(snapshot)
          return null
        }
        return items
      }
      if (loadedEquipments !== this.devicesQtyNumber) {
        this.showAssignmentError(snapshot)
        return null
      }
      return items
    },
    formatFieldSurveyDate (createdAt) {
      const d = firestoreDateToJs(createdAt)
      if (!d) return ''
      const loc = this.$i18n.locale === 'es' ? 'es-AR' : 'en-US'
      return d.toLocaleDateString(loc, { month: 'short', day: 'numeric' })
    },
    markerLetter (index) {
      return surveyWorkMarkerLabel(index)
    },
    showSnackbar (text, color = 'success', timeout = 4000) {
      this.snackbar = { ...this.snackbar, show: true, text, color, timeout }
    },
    teardownSurveyWorksListener () {
      if (this.unsubscribeSurveyWorks) {
        this.unsubscribeSurveyWorks()
        this.unsubscribeSurveyWorks = null
      }
    },
    async bootstrap (fieldSurveyId) {
      this.fieldSurveyLoading = true
      this.fieldSurveyError = false
      this.surveyWorksLoading = true
      this.surveyWorksError = false
      this.surveyWorks = []
      this.technicianItems = []
      this.hasServiceQuantitiesField = false
      this.contractedServices = null
      const user = this.$store.getters.user
      const allowedIds = (user && user.fieldSurveyIds) || []
      const isAdmin = !!(user && user.isAdmin)
      if (!allowedIds.includes(fieldSurveyId) && !isAdmin) {
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_ACCESS_DENIED, { fieldSurveyId })
        this.fieldSurveyLoading = false
        this.surveyWorksLoading = false
        this.fieldSurveyError = true
        return
      }
      try {
        const db = await firestore
        const ref = db.collection(FIELD_SURVEYS).doc(fieldSurveyId)
        const snap = await ref.get()
        if (!snap.exists) {
          trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_NOT_FOUND, { fieldSurveyId })
          this.fieldSurveyError = true
          this.fieldSurveyLoading = false
          this.surveyWorksLoading = false
          return
        }
        const data = snap.data() || {}
        this.fieldSurveyStatus = data.status != null ? String(data.status) : FIELD_SURVEY_STATUS.NEW
        this.fieldSurveyCreatedAt = data.createdAt
        this.fieldSurveyDealId = data.dealId != null ? String(data.dealId) : ''
        this.devicesQty = data.devicesQty
        this.hasServiceQuantitiesField = !!(data.serviceQuantities && typeof data.serviceQuantities === 'object')
        this.contractedServices = serviceQuantities(data)

        if (this.fieldSurveyStatus === FIELD_SURVEY_STATUS.COMPLETED && !this.isPonceAdmin) {
          this.fieldSurveyLoading = false
          this.surveyWorksLoading = false
          const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
          this.$router.replace({ path: '/audits', query })
          return
        }

        if (
          !this.$store.getters.impersonatedUserId &&
          this.fieldSurveyStatus === FIELD_SURVEY_STATUS.NEW
        ) {
          try {
            await markFieldSurveyInProgressForSetup(db, fieldSurveyId)
            this.fieldSurveyStatus = FIELD_SURVEY_STATUS.IN_PROGRESS
          } catch (e) {
            console.error('Field survey status update failed:', e)
          }
        }
        this.fieldSurveyLoading = false
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_FORM_LOADED, {
          fieldSurveyId,
          fieldSurveyDealId: this.fieldSurveyDealId,
          devicesQty: this.devicesQtyNumber
        })

        const q = db.collection(SURVEY_WORKS).where('fieldSurvey', '==', ref)
        this.unsubscribeSurveyWorks = q.onSnapshot(
          (querySnap) => {
            const list = []
            querySnap.forEach((doc) => {
              const d = doc.data() || {}
              const ponceTechnician = !!d.ponceTechnician
              const userId = ponceTechnician ? '' : (parseSurveyWorkUserRefId(d.user) || '')
              list.push({
                id: doc.id,
                equipmentId: d.equipmentId != null ? String(d.equipmentId) : '',
                serviceType: d.serviceType != null ? String(d.serviceType) : '',
                deviceName: d.deviceName != null ? String(d.deviceName) : (d.name != null ? String(d.name) : ''),
                userId,
                ponceTechnician,
                userDisplay: '',
                coordinates: d.coordinates != null ? String(d.coordinates) : '',
                pluviometricChart: d.pluviometricChart != null ? String(d.pluviometricChart) : '',
                status: d.status != null ? String(d.status) : '',
                createdAt: d.createdAt
              })
            })
            list.sort((a, b) => {
              const ta = firestoreDateToJs(a.createdAt)
              const tb = firestoreDateToJs(b.createdAt)
              if (ta && tb) return ta.getTime() - tb.getTime()
              if (ta && !tb) return -1
              if (!ta && tb) return 1
              return a.id.localeCompare(b.id)
            })
            ;(async () => {
              try {
                const dbInner = await firestore
                const refIds = list.filter((r) => r.userId && !r.ponceTechnician).map((r) => r.userId)
                const userMap = await fetchUsersByIds(dbInner, refIds)
                list.forEach((r) => {
                  if (r.ponceTechnician) {
                    r.userDisplay = this.$t('FieldSurveyForm.ponceTechnicianOption')
                  } else if (r.userId && userMap.has(r.userId)) {
                    const u = userMap.get(r.userId)
                    r.userDisplay = formatTechnicianDisplayName(u.name, u.lastName)
                  } else if (r.userId) {
                    r.userDisplay = r.userId
                  } else {
                    r.userDisplay = ''
                  }
                })
                this.surveyWorks = list
                this.surveyWorksLoading = false
                this.surveyWorksError = false
              } catch (enrichErr) {
                console.error('surveyWorks enrich error:', enrichErr)
                list.forEach((r) => {
                  if (r.ponceTechnician) {
                    r.userDisplay = this.$t('FieldSurveyForm.ponceTechnicianOption')
                  }
                })
                this.surveyWorks = list
                this.surveyWorksLoading = false
                this.surveyWorksError = false
              }
            })()
          },
          (err) => {
            console.error('surveyWorks listener error:', err)
            this.surveyWorksError = true
            this.surveyWorksLoading = false
          }
        )
        this.loadTechnicianOptions()
      } catch (e) {
        console.error('FieldSurveyForm bootstrap:', e)
        this.fieldSurveyError = true
        this.fieldSurveyLoading = false
        this.surveyWorksLoading = false
      }
    },
    async onSaveWork ({ services, equipmentId, deviceName, userId, ponceTechnician, coordinates, pluviometricChartChange, pluviometricChartFile, onComplete }) {
      const done = (ok) => {
        if (typeof onComplete === 'function') onComplete(!!ok)
      }
      if (!this.isSetupEditable || this.operationsBusy) {
        done(false)
        return
      }
      if (!this.validateSelection(services, equipmentId)) {
        done(false)
        return
      }
      if (!validateLatLon(String(coordinates || ''))) {
        this.showSnackbar(this.$t('Config.allCoordinatesRule'), 'error')
        done(false)
        return
      }
      const members = this.surveyWorks.filter((work) => equipmentGroupId(work) === equipmentId)
      if (!members.length) {
        done(false)
        return
      }
      if (this.contractedServices && lockedServiceKeys(members).some((key) => !services.includes(key))) {
        done(false)
        return
      }
      this.savingId = equipmentId
      try {
        const db = await firestore
        const previousPaths = chartPaths(members)
        const inheritChart = previousPaths[0] || ''
        const replaceChart = pluviometricChartChange === 'replace' && !!pluviometricChartFile
        const removeChart = pluviometricChartChange === 'remove'
        const touchChart = replaceChart || removeChart
        let nextChartPath = inheritChart
        if (replaceChart) {
          const orgName = this.$store.getters.userOrganization.name || ''
          nextChartPath = await uploadSurveyWorkPluviometricChart(pluviometricChartFile, orgName, members[0].id)
        } else if (removeChart) {
          nextChartPath = ''
        }

        const uid = String(userId || '').trim()
        const technicianChanged = members.some((work) => surveyWorkTechnicianChanged(work, { userId: uid, ponceTechnician }))
        const shared = {
          equipmentId,
          deviceName,
          coordinates,
          updatedAt: firestoreConstructors.FieldValue.serverTimestamp(),
          ...technicianFields(db, { userId: uid, ponceTechnician })
        }
        if (touchChart) shared.pluviometricChart = nextChartPath
        if (technicianChanged) shared[SURVEY_WORK_SHOULD_NOTIFY] = true

        const batch = db.batch()
        const works = db.collection(SURVEY_WORKS)
        if (!this.contractedServices) {
          const work = members[0]
          const payload = { ...shared }
          if (String(work.status || '').trim() === '') payload.status = SURVEY_WORK_STATUS.NEW
          batch.update(works.doc(work.id), payload)
        } else {
          const selectedTypes = services.map((key) => serviceTypeFromKey(key)).filter(Boolean)
          const selectedKeys = selectedTypes.map((serviceType) => serviceKeyFromServiceType(serviceType))
          const removedIds = new Set(
            surveyWorksRemovedByServiceSelection(members, selectedKeys).map((work) => work.id)
          )
          const keptIds = new Set()
          selectedTypes.forEach((serviceType) => {
            const key = serviceKeyFromServiceType(serviceType)
            const existing = members.find((work) => (
              effectiveServiceKey(work) === key && !removedIds.has(work.id)
            ))
            if (existing) {
              const payload = { ...shared }
              if (String(existing.status || '').trim() === '') payload.status = SURVEY_WORK_STATUS.NEW
              if (existing.serviceType !== serviceType) payload.serviceType = serviceType
              batch.update(works.doc(existing.id), payload)
              keptIds.add(existing.id)
              return
            }
            const created = works.doc()
            const payload = {
              ...shared,
              serviceType,
              fieldSurvey: db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId),
              status: SURVEY_WORK_STATUS.NEW,
              [SURVEY_WORK_SHOULD_NOTIFY]: true,
              createdAt: firestoreConstructors.FieldValue.serverTimestamp()
            }
            if (!touchChart && inheritChart) payload.pluviometricChart = inheritChart
            batch.set(created, payload)
          })
          members.forEach((work) => {
            if (keptIds.has(work.id)) return
            if (removedIds.has(work.id) && isSurveyWorkNewStatus(work.status)) {
              batch.delete(works.doc(work.id))
            }
          })
        }
        await batch.commit()

        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_SAVED, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: equipmentId,
          pluviometricChartChange,
          ponceTechnician: !!ponceTechnician
        })

        if (touchChart) await deleteUnusedCharts(previousPaths, nextChartPath)
        this.showSnackbar(this.$t('FieldSurveyForm.saveSuccess'))
        done(true)
      } catch (e) {
        console.error('SurveyWork update failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.saveError'), 'error')
        done(false)
      } finally {
        this.savingId = null
      }
    },
    async onDeleteWork (equipmentId) {
      if (!this.isSetupEditable || this.operationsBusy) return
      const members = this.surveyWorks.filter((work) => equipmentGroupId(work) === equipmentId)
      if (!members.length) return
      this.deletingId = equipmentId
      try {
        const db = await firestore
        const paths = chartPaths(members)
        const batch = db.batch()
        const works = db.collection(SURVEY_WORKS)
        members.forEach((work) => batch.delete(works.doc(work.id)))
        await batch.commit()
        await deleteUnusedCharts(paths, '')
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_DELETED, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: equipmentId
        })
        this.showSnackbar(this.$t('FieldSurveyForm.deleteSuccess'))
      } catch (e) {
        console.error('SurveyWork delete failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.deleteError'), 'error')
      } finally {
        this.deletingId = null
      }
    },
    onConfirmClick () {
      if (this.operationsBusy || !this.validateAssignments()) return
      if (this.hasAnyPonceTechnicianSurveyWork) {
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_PONCE_CHARGE_DIALOG_OPENED, {
          fieldSurveyId: this.fieldSurveyId
        })
        this.ponceChargeConfirmDialog = true
        return
      }
      this.runFieldSurveyConfirmFlow()
    },
    onConfirmPonceCharges () {
      this.ponceChargeConfirmDialog = false
      this.runFieldSurveyConfirmFlow()
    },
    async runFieldSurveyConfirmFlow () {
      if (!this.fieldSurveyId || this.operationsBusy || !this.validateAssignments()) return
      this.mapSaving = true
      try {
        const db = await firestore
        if (!await this.validateServerAssignments(db)) return
        await db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId).update({
          status: 'completed',
          updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
        })
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_COMPLETED, {
          fieldSurveyId: this.fieldSurveyId,
          fieldSurveyDealId: this.fieldSurveyDealId
        })
        this.showSnackbar(this.$t('FieldSurveyForm.confirmSuccess'))
        const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
        this.$router.push({ path: '/audits', query })
      } catch (e) {
        console.error('Field survey confirm failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.confirmError'), 'error')
      } finally {
        this.mapSaving = false
      }
    },
    onTechnicianUserCreated ({ id, name, lastName }) {
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_TECHNICIAN_USER_CREATED, {
        fieldSurveyId: this.fieldSurveyId,
        newUserId: id
      })
      this.technicianItems = mergeTechnicianOption(this.technicianItems, id, name, lastName)
    },
    async loadTechnicianOptions () {
      const orgId = this.organizationId
      if (!orgId || !this.fieldSurveyId) return
      this.technicianOptionsLoading = true
      try {
        const db = await firestore
        const fieldSurveyRef = db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId)
        const items = await loadTechnicianSelectOptions(db, orgId, fieldSurveyRef)
        this.technicianItems = items
      } catch (e) {
        console.error('loadTechnicianOptions:', e)
      } finally {
        this.technicianOptionsLoading = false
      }
    },
    enableAdminEdit () {
      this.adminEditMode = true
    },
    async finishAdminEdit () {
      if (this.operationsBusy || !this.validateAssignments()) return
      this.mapSaving = true
      try {
        const db = await firestore
        if (!await this.validateServerAssignments(db)) return
      } catch (e) {
        this.showSnackbar(this.$t('FieldSurveyForm.confirmError'), 'error')
        return
      } finally {
        this.mapSaving = false
      }
      this.adminEditMode = false
      const query = this.fieldSurveyDealId ? { dealId: this.fieldSurveyDealId } : {}
      this.$router.push({ path: '/audits', query })
    },
    onMapAddBlocked (payload) {
      const q = this.equipmentCapNumber
      const reason = (payload && payload.reason) || (q === null ? 'unknown_qty' : 'at_limit')
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_MAP_ADD_BLOCKED, {
        fieldSurveyId: this.fieldSurveyId,
        reason
      })
      if (reason === 'unknown_qty') {
        this.showSnackbar(this.$t('FieldSurveyForm.mapAddBlockedNoQty'), 'info')
      } else if (reason === 'services_complete') {
        this.showSnackbar(this.$t('FieldSurveyForm.mapAddBlockedServicesComplete'), 'info')
      } else {
        this.showSnackbar(this.$t('FieldSurveyForm.mapAddBlockedLimit'), 'info')
      }
    },
    async onCreateSurveyWorkFromMap ({ services, deviceName, userId, ponceTechnician, coordinates, pluviometricChartFile }) {
      if (!this.fieldSurveyId || !this.isSetupEditable || this.operationsBusy) return
      if (!this.validateSelection(services)) return
      if (!validateLatLon(String(coordinates || ''))) {
        this.showSnackbar(this.$t('Config.allCoordinatesRule'), 'error')
        return
      }
      this.mapSaving = true
      try {
        const db = await firestore
        const uid = String(userId || '').trim()
        const equipmentId = db.collection(SURVEY_WORKS).doc().id
        const serviceTypes = this.contractedServices
          ? services.map((key) => serviceTypeFromKey(key)).filter(Boolean)
          : [null]
        const batch = db.batch()
        const createdRefs = serviceTypes.map((serviceType) => {
          const ref = db.collection(SURVEY_WORKS).doc()
          const payload = {
            equipmentId,
            fieldSurvey: db.collection(FIELD_SURVEYS).doc(this.fieldSurveyId),
            deviceName,
            coordinates,
            status: SURVEY_WORK_STATUS.NEW,
            [SURVEY_WORK_SHOULD_NOTIFY]: true,
            createdAt: firestoreConstructors.FieldValue.serverTimestamp(),
            updatedAt: firestoreConstructors.FieldValue.serverTimestamp(),
            ...technicianFields(db, { userId: uid, ponceTechnician })
          }
          if (serviceType) payload.serviceType = serviceType
          batch.set(ref, payload)
          return ref
        })
        await batch.commit()

        if (pluviometricChartFile && createdRefs.length) {
          const orgName = this.$store.getters.userOrganization.name || ''
          const pluviometricChart = await uploadSurveyWorkPluviometricChart(
            pluviometricChartFile,
            orgName,
            createdRefs[0].id
          )
          const chartBatch = db.batch()
          createdRefs.forEach((ref) => {
            chartBatch.update(ref, {
              pluviometricChart,
              updatedAt: firestoreConstructors.FieldValue.serverTimestamp()
            })
          })
          await chartBatch.commit()
        }
        trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_SURVEY_WORK_CREATED_FROM_MAP, {
          fieldSurveyId: this.fieldSurveyId,
          surveyWorkId: equipmentId,
          ponceTechnician: !!ponceTechnician
        })
        this.showSnackbar(this.$t('FieldSurveyForm.saveSuccess'))
        if (this.$refs.fieldSurveyMap) {
          this.$refs.fieldSurveyMap.notifyCreateSuccess()
        }
      } catch (e) {
        console.error('SurveyWork create failed:', e)
        this.showSnackbar(this.$t('FieldSurveyForm.saveError'), 'error')
      } finally {
        this.mapSaving = false
      }
    }
  }
}
</script>

<style scoped lang="stylus">
$fsf-bg = #f4f7f9
$fsf-primary = #0098E1
$fsf-text = #333333
$fsf-text-secondary = #666666
$fsf-hint-bg = #ffcc00
$fsf-card-radius = 12px

.field-survey-form-root
  /* Fill v-main (already sized below toolbar when toolbar is visible) */
  position relative
  height 100%
  max-height 100%
  min-height 0
  background $fsf-bg
  padding 12px 16px
  box-sizing border-box
  display flex
  flex-direction column
  overflow hidden

.fsf-assignment-toast
  position absolute
  z-index 20
  top 12px
  left 50%
  transform translateX(-50%)
  display flex
  align-items flex-start
  gap 10px
  width auto
  max-width 420px
  padding 10px 10px 10px 12px
  background #fff8f7
  border 1px solid rgba(198, 40, 40, 0.22)
  border-radius 10px
  box-shadow 0 6px 20px rgba(0, 32, 47, 0.12)
  color #333
  pointer-events auto

.fsf-assignment-toast-icon
  flex-shrink 0
  margin-top 1px
  color #c62828 !important

.fsf-assignment-toast-body
  flex 1 1 auto
  min-width 0

.fsf-assignment-toast-title
  margin 0
  font-size 0.875rem
  font-weight 600
  line-height 1.3
  color #333

.fsf-assignment-toast-list
  margin 4px 0 0
  padding 0
  list-style none

.fsf-assignment-toast-list li
  margin 0
  font-size 0.8125rem
  line-height 1.4
  color #666

.fsf-assignment-toast-list li + li
  margin-top 2px

.fsf-assignment-toast-close
  flex-shrink 0
  display inline-flex
  align-items center
  justify-content center
  width 28px
  height 28px
  margin -4px -2px 0 0
  padding 0
  border 0
  border-radius 6px
  background transparent
  color #888
  cursor pointer

.fsf-assignment-toast-close:hover
  background rgba(0, 32, 47, 0.06)
  color #555

.fsf-toast-enter-active
.fsf-toast-leave-active
  transition opacity 0.18s ease, transform 0.18s ease

.fsf-toast-enter
.fsf-toast-leave-to
  opacity 0
  transform translateX(-50%) translateY(-6px)

.field-survey-form-inner
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  width 100%
  max-width 1320px
  margin 0 auto

.fsf-page-header
  flex-shrink 0
  margin-bottom 12px

.fsf-page-title
  font-size 1.5rem
  font-weight 500
  color $fsf-text
  line-height 1.3
  margin 0
  letter-spacing -0.02em

@media (min-width: 960px)
  .fsf-page-title
    font-size 1.65rem

.fsf-main-card
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  background #fff
  border-radius $fsf-card-radius
  box-shadow 0 1px 4px rgba(0, 32, 47, 0.08)
  overflow hidden

@media (min-width: 960px)
  .fsf-main-card
    flex-direction row
    align-items stretch

.fsf-map-panel
  position relative
  flex 1 1 auto
  min-height 0
  background #e8ecef

@media (max-width: 959px)
  .fsf-map-panel
    min-height 240px
    flex 1 1 45%

@media (min-width: 960px)
  .fsf-map-panel
    flex 2 1 0
    min-height 0
    min-width 0

.fsf-map-placeholder
  position absolute
  inset 0
  background linear-gradient(160deg, #dfe6ea 0%, #eef2f5 45%, #e4e9ed 100%)

.fsf-map-embed
  position absolute
  inset 0

.fsf-map-hint-banner
  position absolute
  left 50%
  bottom 20px
  transform translateX(-50%)
  z-index 2
  display flex
  align-items center
  gap 8px
  max-width calc(100% - 32px)
  padding 10px 18px
  background $fsf-hint-bg
  border-radius 999px
  font-size 0.8125rem
  font-weight 500
  color #333
  box-shadow 0 2px 8px rgba(0, 0, 0, 0.12)

.fsf-map-hint-icon
  color #333 !important
  flex-shrink 0

.fsf-sidebar
  flex 1 1 auto
  min-height 0
  display flex
  flex-direction column
  padding 20px 20px 16px
  max-width 100%
  box-sizing border-box
  border-top 1px solid rgba(0, 32, 47, 0.06)
  overflow hidden

@media (min-width: 960px)
  .fsf-sidebar
    flex 1 1 0
    max-width 400px
    min-width 300px
    border-top none
    border-left 1px solid rgba(0, 32, 47, 0.06)

.fsf-sidebar-loading
.fsf-sidebar-error
  flex-shrink 0

.fsf-sidebar-header
  flex-shrink 0
  margin-bottom 16px
  color $fsf-text
  line-height 1.4

.fsf-sidebar-header-top
  display flex
  flex-wrap wrap
  align-items baseline
  column-gap 6px
  row-gap 4px
  font-size 0.9375rem

.fsf-sidebar-count
  margin 0
  font-weight 500

.fsf-summary
  display flex
  flex-direction column
  gap 6px

.fsf-summary-row
  display flex
  align-items baseline
  justify-content space-between
  gap 12px
  min-height 22px

.fsf-summary-label
  flex 1 1 auto
  min-width 0
  font-size 0.9375rem
  font-weight 500
  color $fsf-text

.fsf-summary-metric
  display inline-flex
  align-items baseline
  flex-wrap wrap
  justify-content flex-end
  gap 8px

.fsf-summary-value
  flex-shrink 0
  font-size 0.9375rem
  font-weight 600
  font-variant-numeric tabular-nums
  letter-spacing 0.01em
  color $fsf-text

.fsf-summary-value--complete
  color #2e7d32

.fsf-summary-value--pending
  color #ed6c02

.fsf-summary-value--error
  color #c62828

.fsf-hire-more-link
  display inline
  color $fsf-primary
  font-weight 500
  font-size 0.8125rem
  text-decoration none
  white-space nowrap

.fsf-hire-more-link:hover
  text-decoration underline

.fsf-edit-service-wrap
  flex-shrink 0
  display flex
  justify-content flex-end
  margin-bottom 12px

.fsf-edit-service-link
  color $fsf-primary
  font-weight 500
  font-size 0.9375rem
  text-decoration none
  white-space nowrap

.fsf-edit-service-link:hover
  text-decoration underline

.fsf-confirm-wrap
  flex-shrink 0
  margin-top auto
  padding-top 12px
  width 100%

.fsf-confirm-btn
  text-transform none
  font-weight 600
  font-size 1rem
  letter-spacing 0.02em
  min-height 48px !important
  height auto !important
  padding 12px 20px !important
  background $fsf-primary !important
  color #fff !important

.fsf-confirm-btn.v-btn--disabled
  background #e0e4e8 !important
  color #fff !important
  opacity 1

.fsf-sidebar-list
  flex 1 1 auto
  min-height 0
  overflow-x visible
  overflow-y auto
  -webkit-overflow-scrolling touch
  margin-bottom 16px
  padding 14px 2px 0 12px

.fsf-empty
  margin-top 8px

.fsf-ponce-dialog-card
  position relative
  border-radius 8px
  padding-top 4px

.fsf-ponce-close
  position absolute
  right 4px
  top 4px
  z-index 1

.fsf-ponce-dialog-text
  color #424242
  font-size 0.9375rem
  line-height 1.5
  padding-left 28px !important
  padding-right 28px !important

.fsf-ponce-dialog-actions
  flex-wrap wrap
  justify-content center

.fsf-ponce-btn-cancel
  border-color #e0e0e0 !important
  color #424242 !important
  text-transform none

.fsf-ponce-btn-ok
  text-transform none
  margin-left 12px !important
</style>



// WEBPACK FOOTER //
// FieldSurveyForm.vue?8ffc96d2